// src/cognito.ts
var SignInError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "SignInError";
  }
};
function cognitoConfig(env) {
  const domain = String(env.VITE_COGNITO_DOMAIN ?? "").trim().replace(/\/+$/, "");
  const clientId = String(env.VITE_COGNITO_CLIENT_ID ?? "").trim();
  const redirectUri = String(env.VITE_COGNITO_REDIRECT_URI ?? "").trim();
  if (!domain || !clientId || !redirectUri) return null;
  const signOutUri = String(env.VITE_COGNITO_SIGNOUT_URI ?? "").trim() || new URL("/", redirectUri).toString();
  return { domain, clientId, redirectUri, signOutUri };
}
function b64url(bytes) {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function randomString(byteLength = 32) {
  const bytes = new Uint8Array(byteLength);
  globalThis.crypto.getRandomValues(bytes);
  return b64url(bytes);
}
async function challengeFor(verifier) {
  const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return b64url(new Uint8Array(digest));
}
var PENDING_KEY = "bioweave_cognito_pending";
async function authorizeUrl(cfg, pending = sessionStorage) {
  const verifier = randomString(32);
  const state = randomString(16);
  pending.setItem(PENDING_KEY, JSON.stringify({ verifier, state }));
  const q = new URLSearchParams({
    response_type: "code",
    client_id: cfg.clientId,
    redirect_uri: cfg.redirectUri,
    scope: "openid email",
    code_challenge: await challengeFor(verifier),
    code_challenge_method: "S256",
    state
  });
  return `${cfg.domain}/oauth2/authorize?${q.toString()}`;
}
async function beginSignIn(cfg, pending = sessionStorage, go = (u) => window.location.assign(u)) {
  go(await authorizeUrl(cfg, pending));
}
async function tokenRequest(cfg, body, fetchFn) {
  const res = await fetchFn(`${cfg.domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ client_id: cfg.clientId, ...body }).toString()
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new SignInError(`token endpoint: ${String(data.error ?? res.status)}`);
  return data;
}
function tokensFrom(data, keepRefresh) {
  const accessToken = String(data.access_token ?? "");
  if (!accessToken) throw new SignInError("token endpoint returned no access token");
  const exp = accessExpiry(accessToken);
  if (exp === null) throw new SignInError("access token carries no expiry");
  const refresh = typeof data.refresh_token === "string" && data.refresh_token ? data.refresh_token : keepRefresh;
  return { accessToken, refreshToken: refresh, expiresAt: exp };
}
async function completeSignIn(cfg, search, pending = sessionStorage, fetchFn = fetch) {
  const params = new URLSearchParams(search);
  const raw = pending.getItem(PENDING_KEY);
  pending.removeItem(PENDING_KEY);
  if (params.get("error")) throw new SignInError(`sign-in refused: ${params.get("error")}`);
  const code = params.get("code");
  if (!code) throw new SignInError("no authorization code");
  let saved = {};
  try {
    saved = raw ? JSON.parse(raw) : {};
  } catch {
    saved = {};
  }
  if (!saved.verifier || !saved.state || saved.state !== params.get("state")) {
    throw new SignInError("sign-in state does not match this tab");
  }
  const data = await tokenRequest(cfg, {
    grant_type: "authorization_code",
    code,
    redirect_uri: cfg.redirectUri,
    code_verifier: saved.verifier
  }, fetchFn);
  return tokensFrom(data, null);
}
async function refreshTokens(cfg, refreshToken, fetchFn = fetch) {
  const data = await tokenRequest(cfg, { grant_type: "refresh_token", refresh_token: refreshToken }, fetchFn);
  return tokensFrom(data, refreshToken);
}
function signOutUrl(cfg) {
  const q = new URLSearchParams({ client_id: cfg.clientId, logout_uri: cfg.signOutUri });
  return `${cfg.domain}/logout?${q.toString()}`;
}
function accessExpiry(token) {
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "="));
    const exp = JSON.parse(json).exp;
    return typeof exp === "number" ? exp * 1e3 : null;
  } catch {
    return null;
  }
}
function makeRefreshStore(key, store) {
  return {
    load() {
      return store()?.getItem(key) ?? null;
    },
    save(token) {
      store()?.setItem(key, token);
    },
    clear() {
      store()?.removeItem(key);
    }
  };
}
var CognitoAuthError = class extends Error {
  constructor(kind, code, message) {
    super(message);
    this.kind = kind;
    this.code = code;
    this.name = "CognitoAuthError";
  }
};
var KIND_BY_CODE = {
  LimitExceededException: "limit",
  TooManyRequestsException: "limit",
  TooManyFailedAttemptsException: "limit",
  CodeMismatchException: "code",
  ExpiredCodeException: "expired",
  RefreshTokenReuseException: "signed-out"
};
function kindFor(code, call) {
  if (KIND_BY_CODE[code]) return KIND_BY_CODE[code];
  if (call === "InitiateAuth" && (code === "NotAuthorizedException" || code === "UserNotFoundException")) return "other";
  if (call === "GetTokensFromRefreshToken" && [
    "NotAuthorizedException",
    "UserNotFoundException",
    "ResourceNotFoundException",
    "InvalidParameterException"
  ].includes(code)) return "signed-out";
  if (code === "NotAuthorizedException") return "expired";
  if (code === "UserNotFoundException") return "code";
  return "other";
}
function cognitoPool(env) {
  const region = String(env.VITE_COGNITO_REGION ?? "").trim();
  const clientId = String(env.VITE_COGNITO_CLIENT_ID ?? "").trim();
  return region && clientId ? { region, clientId } : null;
}
async function idp(pool, call, body, fetchFn) {
  let res;
  try {
    res = await fetchFn(`https://cognito-idp.${pool.region}.amazonaws.com/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target": `AWSCognitoIdentityProviderService.${call}`
      },
      body: JSON.stringify(body)
    });
  } catch (e) {
    throw new CognitoAuthError("unavailable", "NetworkError", `${call}: ${e?.message ?? "network error"}`);
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const code = String(data.__type ?? data.code ?? `HTTP${res.status}`).split("#").pop() ?? "";
    const kind = res.status >= 500 ? "unavailable" : kindFor(code, call);
    throw new CognitoAuthError(kind, code, `${call}: ${code}`);
  }
  return data;
}
function tokensFromResult(data, keepRefresh) {
  const r = data.AuthenticationResult ?? {};
  const accessToken = String(r.AccessToken ?? "");
  if (!accessToken) throw new CognitoAuthError("other", "NoAccessToken", "Cognito returned no access token");
  const exp = accessExpiry(accessToken) ?? (typeof r.ExpiresIn === "number" ? Date.now() + r.ExpiresIn * 1e3 : null);
  if (exp === null) throw new CognitoAuthError("other", "NoExpiry", "access token carries no expiry");
  const refresh = typeof r.RefreshToken === "string" && r.RefreshToken ? r.RefreshToken : keepRefresh;
  return { accessToken, refreshToken: refresh, expiresAt: exp };
}
async function startSignIn(pool, email, fetchFn = fetch) {
  const username = (email || "").trim().toLowerCase();
  if (!username) throw new CognitoAuthError("other", "NoEmail", "no email");
  const data = await idp(pool, "InitiateAuth", {
    AuthFlow: "USER_AUTH",
    ClientId: pool.clientId,
    AuthParameters: { USERNAME: username, PREFERRED_CHALLENGE: "EMAIL_OTP" }
  }, fetchFn);
  if (data.ChallengeName !== "EMAIL_OTP" || typeof data.Session !== "string") {
    throw new CognitoAuthError(
      "other",
      String(data.ChallengeName ?? "NoChallenge"),
      `expected an email code challenge, got ${String(data.ChallengeName)}`
    );
  }
  return { username, session: data.Session };
}
async function submitCode(pool, challenge, code, fetchFn = fetch) {
  const data = await idp(pool, "RespondToAuthChallenge", {
    ChallengeName: "EMAIL_OTP",
    ClientId: pool.clientId,
    Session: challenge.session,
    ChallengeResponses: { USERNAME: challenge.username, EMAIL_OTP_CODE: code.trim() }
  }, fetchFn);
  if (!data.AuthenticationResult) {
    throw new CognitoAuthError(
      "other",
      String(data.ChallengeName ?? "NoResult"),
      `no tokens after the code (next challenge ${String(data.ChallengeName)})`
    );
  }
  return tokensFromResult(data, null);
}
async function refreshWith(pool, refreshToken, fetchFn = fetch) {
  const data = await idp(
    pool,
    "GetTokensFromRefreshToken",
    { RefreshToken: refreshToken, ClientId: pool.clientId },
    fetchFn
  );
  return tokensFromResult(data, refreshToken);
}
async function revoke(pool, refreshToken, fetchFn = fetch) {
  try {
    await idp(pool, "RevokeToken", { Token: refreshToken, ClientId: pool.clientId }, fetchFn);
    return true;
  } catch {
    return false;
  }
}
function b64urlToBytes(s) {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4);
  const bin = atob(b64);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}
function bytesToB64url(buf) {
  const bytes = buf instanceof ArrayBuffer ? new Uint8Array(buf) : new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
  return b64url(bytes);
}
function creationOptionsFromJSON(o) {
  const native = globalThis.PublicKeyCredential?.parseCreationOptionsFromJSON;
  if (native) return native(o);
  const user = o.user;
  return {
    ...o,
    challenge: b64urlToBytes(String(o.challenge)),
    user: { ...user, id: b64urlToBytes(String(user.id)) },
    excludeCredentials: (o.excludeCredentials ?? []).map((c) => ({ ...c, id: b64urlToBytes(String(c.id)) }))
  };
}
function requestOptionsFromJSON(o) {
  const native = globalThis.PublicKeyCredential?.parseRequestOptionsFromJSON;
  if (native) return native(o);
  return {
    ...o,
    challenge: b64urlToBytes(String(o.challenge)),
    allowCredentials: (o.allowCredentials ?? []).map((c) => ({ ...c, id: b64urlToBytes(String(c.id)) }))
  };
}
function credentialToJSON(cred) {
  const pk = cred;
  if (typeof pk.toJSON === "function") return pk.toJSON();
  const r = pk.response;
  const response = { clientDataJSON: bytesToB64url(r.clientDataJSON) };
  if (r.attestationObject) {
    response.attestationObject = bytesToB64url(r.attestationObject);
    if (typeof r.getTransports === "function") response.transports = r.getTransports();
  }
  if (r.authenticatorData) response.authenticatorData = bytesToB64url(r.authenticatorData);
  if (r.signature) response.signature = bytesToB64url(r.signature);
  if (r.userHandle) response.userHandle = bytesToB64url(r.userHandle);
  return {
    id: pk.id,
    rawId: bytesToB64url(pk.rawId),
    type: pk.type,
    response,
    authenticatorAttachment: pk.authenticatorAttachment ?? void 0,
    clientExtensionResults: typeof pk.getClientExtensionResults === "function" ? pk.getClientExtensionResults() : {}
  };
}
async function startPasskeySignIn(pool, email, fetchFn = fetch) {
  const username = (email || "").trim().toLowerCase();
  if (!username) throw new CognitoAuthError("other", "NoEmail", "no email");
  const data = await idp(pool, "InitiateAuth", {
    AuthFlow: "USER_AUTH",
    ClientId: pool.clientId,
    AuthParameters: { USERNAME: username, PREFERRED_CHALLENGE: "WEB_AUTHN" }
  }, fetchFn);
  const params = data.ChallengeParameters ?? {};
  if (data.ChallengeName !== "WEB_AUTHN" || typeof data.Session !== "string" || typeof params.CREDENTIAL_REQUEST_OPTIONS !== "string") {
    throw new CognitoAuthError("other", "NoPasskey", `no passkey challenge (got ${String(data.ChallengeName)})`);
  }
  return { username, session: data.Session, requestOptions: JSON.parse(params.CREDENTIAL_REQUEST_OPTIONS) };
}
async function submitPasskey(pool, challenge, credential, fetchFn = fetch) {
  const data = await idp(pool, "RespondToAuthChallenge", {
    ChallengeName: "WEB_AUTHN",
    ClientId: pool.clientId,
    Session: challenge.session,
    ChallengeResponses: { USERNAME: challenge.username, CREDENTIAL: JSON.stringify(credential) }
  }, fetchFn);
  if (!data.AuthenticationResult) {
    throw new CognitoAuthError("other", String(data.ChallengeName ?? "NoResult"), "no tokens after the passkey");
  }
  return tokensFromResult(data, null);
}
async function hasPasskey(pool, accessToken, fetchFn = fetch) {
  const data = await idp(pool, "ListWebAuthnCredentials", { AccessToken: accessToken }, fetchFn);
  return Array.isArray(data.Credentials) && data.Credentials.length > 0;
}
function epochMs(v) {
  if (typeof v === "number" && Number.isFinite(v)) return v * 1e3;
  if (typeof v === "string") {
    const t = Date.parse(v);
    return Number.isNaN(t) ? null : t;
  }
  return null;
}
async function listPasskeys(pool, accessToken, fetchFn = fetch) {
  const out = [];
  let next;
  do {
    const data = await idp(
      pool,
      "ListWebAuthnCredentials",
      { AccessToken: accessToken, ...next ? { NextToken: next } : {} },
      fetchFn
    );
    for (const c of Array.isArray(data.Credentials) ? data.Credentials : []) {
      if (typeof c.CredentialId !== "string") continue;
      const name = typeof c.FriendlyCredentialName === "string" && c.FriendlyCredentialName.trim() ? c.FriendlyCredentialName.trim() : null;
      out.push({ id: c.CredentialId, name, createdAt: epochMs(c.CreatedAt) });
    }
    next = typeof data.NextToken === "string" && data.NextToken ? data.NextToken : void 0;
  } while (next);
  return out.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
}
async function deletePasskey(pool, accessToken, credentialId, fetchFn = fetch) {
  await idp(pool, "DeleteWebAuthnCredential", { AccessToken: accessToken, CredentialId: credentialId }, fetchFn);
}
async function signInWithPasskey(pool, email, creds, fetchFn = fetch) {
  const challenge = await startPasskeySignIn(pool, email, fetchFn);
  const cred = await creds.get({ publicKey: requestOptionsFromJSON(challenge.requestOptions) });
  if (!cred) throw new CognitoAuthError("other", "PasskeyCancelled", "no passkey was chosen");
  return submitPasskey(pool, challenge, credentialToJSON(cred), fetchFn);
}
async function createPasskey(pool, accessToken, creds, fetchFn = fetch) {
  const started = await idp(pool, "StartWebAuthnRegistration", { AccessToken: accessToken }, fetchFn);
  const options = started.CredentialCreationOptions;
  if (!options) throw new CognitoAuthError("other", "NoCreationOptions", "Cognito returned no creation options");
  const cred = await creds.create({ publicKey: creationOptionsFromJSON(options) });
  if (!cred) throw new CognitoAuthError("other", "PasskeyCancelled", "no passkey was created");
  await idp(pool, "CompleteWebAuthnRegistration", { AccessToken: accessToken, Credential: credentialToJSON(cred) }, fetchFn);
}
async function passkeysOnThisDevice() {
  const PK = globalThis.PublicKeyCredential;
  if (!PK?.isUserVerifyingPlatformAuthenticatorAvailable) return false;
  try {
    return await PK.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}
export {
  CognitoAuthError,
  SignInError,
  accessExpiry,
  authorizeUrl,
  beginSignIn,
  challengeFor,
  cognitoConfig,
  cognitoPool,
  completeSignIn,
  createPasskey,
  creationOptionsFromJSON,
  credentialToJSON,
  deletePasskey,
  hasPasskey,
  listPasskeys,
  makeRefreshStore,
  passkeysOnThisDevice,
  randomString,
  refreshTokens,
  refreshWith,
  requestOptionsFromJSON,
  revoke,
  signInWithPasskey,
  signOutUrl,
  startPasskeySignIn,
  startSignIn,
  submitCode,
  submitPasskey
};
//# sourceMappingURL=cognito.js.map